/**
 * useMyMemberships — web client hook for the caller's own tenant Memberships
 * (tenant-foundation Task 11, Req 19.1–19.3). Backs the TenantSwitcher and the
 * post-login tenant resolution.
 *
 * Wraps the SELF-ONLY `listMyMemberships` query: the resolver derives the
 * caller's personId from their token `sub`, so it can never enumerate another
 * person's tenants. The list is returned already ordered most-recently-active
 * first (the `lastActiveAt`-driven default landing).
 *
 * Follows the season-ssot hook convention (`useActiveSeason`): the query string
 * is defined LOCALLY (the web codegen client does not include it — the schema is
 * hand-mirrored), a module-scope `generateClient()` handle is reused, calls run
 * `authMode: 'userPool'` (the field is `@aws_cognito_user_pools` only), and a
 * module-level memoized-promise cache shares ONE round-trip across concurrent
 * mounts. `refetch()` / `invalidateMyMembershipsCache()` bust it — call after a
 * join or switch that changes the membership set or its ordering.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { useAuth } from '@/hooks/useAuth';
import type { MembershipSummary } from '@/lib/tenantResolution';

export type { MembershipSummary } from '@/lib/tenantResolution';

// ---------------------------------------------------------------------------
// GraphQL query string (defined locally — not in the web codegen client).
// ---------------------------------------------------------------------------

const LIST_MY_MEMBERSHIPS = /* GraphQL */ `
  query ListMyMemberships {
    listMyMemberships {
      success
      message
      memberships {
        tenantId
        tenantName
        tenantStatus
        roles
        membershipStatus
        lastActiveAt
      }
    }
  }
`;

interface ListMyMembershipsData {
  listMyMemberships: {
    success: boolean;
    message: string;
    memberships: MembershipSummary[] | null;
  } | null;
}

/** Resolution status of the memberships hook. */
export type MembershipResolutionStatus = 'loading' | 'resolved' | 'unresolved';

export interface UseMyMembershipsResult {
  /** The caller's Memberships (most-recently-active first), or `[]` while loading / on error. */
  memberships: MembershipSummary[];
  status: MembershipResolutionStatus;
  /** Bust the cache and re-run the query (call after a join / switch). */
  refetch: () => void;
}

// Module-level client — safe to construct once at import time (matches the
// season-ssot / sidebar hooks).
const client = generateClient();

// Module-level memoized-promise cache (see file header for rationale).
let membershipsPromise: Promise<MembershipSummary[]> | null = null;

function fetchMyMemberships(): Promise<MembershipSummary[]> {
  if (!membershipsPromise) {
    membershipsPromise = (
      client.graphql({
        query: LIST_MY_MEMBERSHIPS,
        authMode: 'userPool',
      }) as Promise<GraphQLResult<ListMyMembershipsData>>
    )
      .then((res) => res.data?.listMyMemberships?.memberships ?? [])
      .catch((err) => {
        // Bust the cache so the next mount re-attempts rather than
        // permanently serving a rejected promise.
        membershipsPromise = null;
        throw err;
      });
  }
  return membershipsPromise;
}

/** Bust the cached `listMyMemberships` promise (after a join / switch). */
export function invalidateMyMembershipsCache(): void {
  membershipsPromise = null;
}

/**
 * Resolve the caller's own tenant Memberships.
 */
export function useMyMemberships(): UseMyMembershipsResult {
  const [memberships, setMemberships] = useState<MembershipSummary[]>([]);
  const [status, setStatus] = useState<MembershipResolutionStatus>('loading');
  // Self-only query — a caller with no session has no memberships. Gate the
  // fetch on auth so app-wide consumers (e.g. BrandingContext) don't fire an
  // authenticated query on public/pre-auth pages.
  const { isAuthenticated } = useAuth();

  const mountedRef = useRef<boolean>(true);
  const fetchIdRef = useRef<number>(0);

  const runFetch = useCallback(async () => {
    const id = ++fetchIdRef.current;
    if (!isAuthenticated) {
      if (mountedRef.current) {
        setMemberships([]);
        setStatus('unresolved');
      }
      return;
    }
    if (mountedRef.current) setStatus('loading');
    try {
      const result = await fetchMyMemberships();
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      setMemberships(result);
      setStatus(result.length > 0 ? 'resolved' : 'unresolved');
    } catch {
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      setMemberships([]);
      setStatus('unresolved');
    }
  }, [isAuthenticated]);

  const refetch = useCallback(() => {
    invalidateMyMembershipsCache();
    void runFetch();
  }, [runFetch]);

  useEffect(() => {
    mountedRef.current = true;
    void runFetch();
    return () => {
      mountedRef.current = false;
    };
  }, [runFetch]);

  return { memberships, status, refetch };
}
